import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";

import usePrivateLink from "../config/usePrivateLink";

import {
  kitAssignmentFeeDetails,
  kitAssignmentFeePay,
} from "../../api/DistributionKit";

import { BankList } from "../../api/BankService";
import { studentFeeReceiptNo } from "../../api/Student";

import DistributionKitLayout from "./DistributionKitLayout";

const splitReceiptNo = (value) => {
  const match = String(value ?? "").match(/^(\D*)(\d+)$/);

  if (match) {
    return {
      prefix: match[1],
      number: match[2],
    };
  }

  return {
    prefix: "",
    number: value ?? "",
  };
};

export default function DistributionKitPayment() {
  usePrivateLink();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const feeId = searchParams.get("id");

  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [fee, setFee] = useState(null);
  const [banks, setBanks] = useState([]);

  const [receiptNoData, setReceiptNoData] = useState({});

  const [receiptParts, setReceiptParts] = useState({
    prefix: "",
    number: "",
  });

  const [formData, setFormData] = useState({
    receipt_no: "",
    amount: "",
    bank_id: "",
    transaction_no: "",
    payment_date: "",
    remark: "",
  });

  /*
   * ----------------------------------------------------
   * Get Fee Details
   * ----------------------------------------------------
   */

  const getFeeDetails = async () => {
    if (!feeId) {
      SweetAlertShow(
        "info",
        "Invalid Payment",
        "Payment record was not specified.",
      );

      navigate("/distribution-kits/report");
      return;
    }

    setLoading(true);

    try {
      const response = await kitAssignmentFeeDetails(feeId);

      if (response?.data?.success) {
        const data = response?.data?.data;

        setFee(data);

        setFormData((prev) => ({
          ...prev,
          amount: Number(data?.due_amount || 0).toFixed(2),
        }));
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load payment details.",
        );

        navigate("/distribution-kits/report");
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load payment details.",
      );

      navigate("/distribution-kits/report");
    } finally {
      setLoading(false);
    }
  };

  /*
   * ----------------------------------------------------
   * Get Banks
   * ----------------------------------------------------
   */

  const getBankList = async () => {
    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load bank accounts.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load bank accounts.",
      );
    }
  };

  /*
   * ----------------------------------------------------
   * Receipt Number
   * ----------------------------------------------------
   */

  const getReceiptNumber = async () => {
    try {
      const response = await studentFeeReceiptNo(
        `receipt_no=${encodeURIComponent("RCPT-1")}`,
      );

      if (response?.data?.status) {
        const data = response?.data?.data || {};

        setReceiptNoData(data);

        const nextReceipt = data?.next_receipt_no || "RCPT-1";

        setFormData((prev) => ({
          ...prev,
          receipt_no: nextReceipt,
        }));

        setReceiptParts(splitReceiptNo(nextReceipt));
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to generate receipt number.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to generate receipt number.",
      );
    }
  };

  /*
   * ----------------------------------------------------
   * Initial
   * ----------------------------------------------------
   */

  useEffect(() => {
    getFeeDetails();
    getBankList();
    getReceiptNumber();

    const today = new Date();

    setFormData((prev) => ({
      ...prev,
      payment_date: today.toISOString().split("T")[0],
    }));
  }, [feeId]);

  /*
   * ----------------------------------------------------
   * Receipt Number Change
   * ----------------------------------------------------
   */

  const handleReceiptNumberChange = (value) => {
    const digitsOnly = String(value || "").replace(/\D/g, "");

    setReceiptParts((prev) => {
      const updated = {
        ...prev,
        number: digitsOnly,
      };

      setFormData((form) => ({
        ...form,
        receipt_no: `${updated.prefix}${updated.number}`,
      }));

      return updated;
    });
  };

  /*
   * ----------------------------------------------------
   * Payment Amount
   * ----------------------------------------------------
   */

  const handleAmountChange = (value) => {
    let amount = Number(value || 0);

    const due = Number(fee?.due_amount || 0);

    if (amount < 0) {
      amount = 0;
    }

    if (amount > due) {
      amount = due;
    }

    setFormData((prev) => ({
      ...prev,
      amount: amount,
    }));
  };

  /*
   * ----------------------------------------------------
   * Submit Payment
   * ----------------------------------------------------
   */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fee) {
      return;
    }

    const amount = Number(formData.amount || 0);
    const due = Number(fee.due_amount || 0);

    if (amount <= 0) {
      SweetAlertShow(
        "info",
        "Invalid Amount",
        "Please enter a valid payment amount.",
      );
      return;
    }

    if (amount > due) {
      SweetAlertShow(
        "info",
        "Invalid Amount",
        `Payment amount cannot exceed ₹${due.toFixed(2)}.`,
      );
      return;
    }

    if (!formData.bank_id) {
      SweetAlertShow(
        "info",
        "Bank Required",
        "Please select the payment account.",
      );
      return;
    }

    if (!formData.receipt_no) {
      SweetAlertShow(
        "info",
        "Receipt Required",
        "Please enter the receipt number.",
      );
      return;
    }

    setPaymentLoading(true);

    try {
      const payload = {
        amount,
        payment_date: formData.payment_date,
        bank_id: formData.bank_id,
        receipt_no: formData.receipt_no,
        transaction_no: formData.transaction_no || null,
        remark: formData.remark || null,
      };

      const response = await kitAssignmentFeePay(fee.id, payload);

      if (response?.data?.success) {
        const paymentData = response?.data?.data;

        /*
         * Payment completed.
         *
         * We now navigate to a dedicated receipt
         * screen instead of opening a modal.
         */
        SweetAlertShow(
          "success",
          "Payment Successful",
          response?.data?.message || "Payment received successfully.",
        );

        navigate(
          `/distribution-kits/payment-receipt?id=${paymentData?.transaction?.id}`,
          {
            state: {
              payment: paymentData,
            },
          },
        );
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Payment could not be completed.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Payment Failed",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to process payment.",
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const recipient = fee?.student
    ? {
        name: fee.student.name || fee.student.student_name || "Student",
        type: "Student",
      }
    : fee?.staff
      ? {
          name: fee.staff.name || fee.staff.staff_name || "Staff",
          type: "Staff",
        }
      : {
          name: "—",
          type: "—",
        };

  const finalAmount = Number(fee?.final_amount || 0);

  const paidAmount = Number(fee?.paid_amount || 0);

  const dueAmount = Number(fee?.due_amount || 0);

  const paymentAmount = Number(formData.amount || 0);

  const remainingAfterPayment = Math.max(0, dueAmount - paymentAmount);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid flex-grow-1 container-p-y">
              <DistributionKitLayout />

              {/* =====================================================
                  HEADER
              ====================================================== */}

              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <button
                        type="button"
                        className="btn btn-label-secondary"
                        onClick={() => navigate("/distribution-kits/report")}
                      >
                        <i className="ri-arrow-left-line" />
                      </button>

                      <div
                        className="rounded-3 bg-label-success d-flex align-items-center justify-content-center"
                        style={{
                          width: 54,
                          height: 54,
                        }}
                      >
                        <i className="ri-money-rupee-circle-line fs-3 text-success" />
                      </div>

                      <div>
                        <h4 className="mb-1 fw-bold">
                          Receive Distribution Payment
                        </h4>

                        <p className="text-muted mb-0">
                          Record payment against the distribution kit fee
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {fee && (
                <form onSubmit={handleSubmit}>
                  <div className="row g-4">
                    {/* =================================================
                        LEFT - FEE DETAILS
                    ================================================== */}

                    <div className="col-xl-5">
                      {/* Recipient */}

                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header border-bottom py-3">
                          <h5 className="mb-0 fw-bold">Recipient Details</h5>
                        </div>

                        <div className="card-body">
                          <div className="d-flex align-items-center gap-3 mb-4">
                            <div
                              className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center"
                              style={{
                                width: 54,
                                height: 54,
                              }}
                            >
                              <i className="ri-user-line fs-4 text-primary" />
                            </div>

                            <div>
                              <div className="fw-bold fs-5">
                                {recipient.name}
                              </div>

                              <div className="text-muted">{recipient.type}</div>
                            </div>
                          </div>

                          <div className="row g-3">
                            <div className="col-6">
                              <div className="text-muted small">Kit</div>

                              <div className="fw-semibold">
                                {fee?.kit?.name || "—"}
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="text-muted small">Item</div>

                              <div className="fw-semibold">
                                {fee?.item?.name || "Complete Kit"}
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="text-muted small">Batch</div>

                              <div className="fw-semibold">
                                {fee?.batch?.title || "—"}
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="text-muted small">Quantity</div>

                              <div className="fw-semibold">
                                {fee?.quantity || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fee Summary */}

                      <div className="card border-0 shadow-sm">
                        <div className="card-header border-bottom py-3">
                          <h5 className="mb-0 fw-bold">Fee Summary</h5>
                        </div>

                        <div className="card-body">
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Kit Amount</span>

                            <span className="fw-semibold">
                              ₹{Number(fee?.kit_amount || 0).toFixed(2)}
                            </span>
                          </div>

                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Discount</span>

                            <span className="text-success">
                              - ₹{Number(fee?.discount_amount || 0).toFixed(2)}
                            </span>
                          </div>

                          <div className="border-top pt-3 mb-3 d-flex justify-content-between">
                            <span className="fw-semibold">Final Amount</span>

                            <span className="fw-bold">
                              ₹{finalAmount.toFixed(2)}
                            </span>
                          </div>

                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Already Paid</span>

                            <span className="text-success fw-semibold">
                              ₹{paidAmount.toFixed(2)}
                            </span>
                          </div>

                          <div className="rounded-3 bg-label-danger p-3 d-flex justify-content-between align-items-center">
                            <span className="fw-semibold">Outstanding</span>

                            <span className="fs-5 fw-bold text-danger">
                              ₹{dueAmount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        RIGHT - PAYMENT
                    ================================================== */}

                    <div className="col-xl-7">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-label-primary border-0 py-3">
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-circle bg-primary d-flex align-items-center justify-content-center"
                              style={{
                                width: 40,
                                height: 40,
                              }}
                            >
                              <i className="ri-bank-card-line text-white" />
                            </div>

                            <div>
                              <h5 className="mb-0 text-primary">
                                Payment Details
                              </h5>

                              <small className="text-muted">
                                Enter payment information
                              </small>
                            </div>
                          </div>
                        </div>

                        <div className="card-body p-4">
                          <div className="row g-3">
                            {/* Receipt */}

                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Receipt No <sup className="text-danger">*</sup>
                              </label>

                              <div className="input-group">
                                {receiptParts.prefix && (
                                  <span className="input-group-text fw-semibold">
                                    {receiptParts.prefix}
                                  </span>
                                )}

                                <input
                                  type="text"
                                  inputMode="numeric"
                                  className="form-control fw-semibold"
                                  value={receiptParts.number}
                                  onChange={(e) =>
                                    handleReceiptNumberChange(e.target.value)
                                  }
                                  required
                                />
                              </div>

                              {receiptNoData?.message && (
                                <div className="text-danger small mt-1">
                                  {receiptNoData.message}
                                </div>
                              )}
                            </div>

                            {/* Amount */}

                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Payment Amount{" "}
                                <sup className="text-danger">*</sup>
                              </label>

                              <div className="input-group">
                                <span className="input-group-text">₹</span>

                                <input
                                  type="number"
                                  className="form-control fw-bold"
                                  min="0.01"
                                  max={dueAmount}
                                  step="0.01"
                                  value={formData.amount}
                                  onChange={(e) =>
                                    handleAmountChange(e.target.value)
                                  }
                                  required
                                />
                              </div>

                              <div className="form-text">
                                Maximum: ₹{dueAmount.toFixed(2)}
                              </div>
                            </div>

                            {/* Bank */}

                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Payment Account{" "}
                                <sup className="text-danger">*</sup>
                              </label>

                              <select
                                className="form-select"
                                value={formData.bank_id}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    bank_id: e.target.value,
                                  }))
                                }
                                required
                              >
                                <option value="">Select Account</option>

                                {banks.map((bank) => (
                                  <option key={bank.id} value={bank.id}>
                                    {bank.account_no} ({bank.owner_name})
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Transaction */}

                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Transaction No
                              </label>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Transaction / Reference No"
                                value={formData.transaction_no}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    transaction_no: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            {/* Payment Date */}

                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Payment Date{" "}
                                <sup className="text-danger">*</sup>
                              </label>

                              <input
                                type="date"
                                className="form-control"
                                value={formData.payment_date}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    payment_date: e.target.value,
                                  }))
                                }
                                required
                              />
                            </div>

                            {/* Remark */}

                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Remark
                              </label>

                              <textarea
                                className="form-control"
                                rows="1"
                                placeholder="Enter remark"
                                value={formData.remark}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    remark: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          {/* Payment Calculation */}

                          <div className="border rounded-3 p-4 mt-4 bg-label-success">
                            <div className="row align-items-center">
                              <div className="col-md-6">
                                <div className="text-muted small">
                                  Payment Amount
                                </div>

                                <div className="fs-3 fw-bold text-success">
                                  ₹{paymentAmount.toFixed(2)}
                                </div>
                              </div>

                              <div className="col-md-6 text-md-end">
                                <div className="text-muted small">
                                  Remaining Due
                                </div>

                                <div className="fs-4 fw-bold">
                                  ₹{remainingAfterPayment.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Submit */}

                          <div className="border-top pt-4 mt-4 d-flex justify-content-between align-items-center">
                            <button
                              type="button"
                              className="btn btn-label-secondary"
                              onClick={() =>
                                navigate("/distribution-kits/report")
                              }
                              disabled={paymentLoading}
                            >
                              <i className="ri-arrow-left-line me-1" />
                              Back
                            </button>

                            <button
                              type="submit"
                              className="btn btn-success px-4"
                              disabled={paymentLoading}
                            >
                              {paymentLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i className="ri-check-line me-1" />
                                  Receive Payment
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
